import React, { useCallback, useEffect, useMemo } from 'react';
import styles from './style.module.scss';
import useCommonStore from '../../../../stores/common';
import useManagerApprovalStore from '../../../../stores/managerApproval';
import ButtonIcon from '../../../../components/ButtonIcon/ButtonIcon';
import ExpandDetailsIcon from '../../../../assets/svg/icons/common/ExpandDetailsIcon';
import CollapseDetailsIcon from '../../../../assets/svg/icons/common/CollapseDetailsIcon';
import CloseIcon24 from '../../../../assets/svg/icons/common/CloseIcon24';
import { fetchAgentPowerappApprovals } from '../../../../services/managerApproval';
import toast from 'react-hot-toast';
import { Copier } from '../../../../../components/copier';
import CopyIcon from '../../../../assets/svg/icons/common/copy';

const AgentApprovalDetails = () => {
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const toggleExpandDetails = useCommonStore((state) => state.toggleExpandDetails);
  const toggleShowDetails = useCommonStore((state) => state.toggleShowDetails);
  const {
    powerappApprovals,
    selectedPowerappId,
    selectedPowerapp,
    currentPage,
    itemsPerPage,
    setSelectedPowerappId,
    setSelectedPowerapp,
    setPowerappApprovals,
  } = useManagerApprovalStore();

  // Load initial data when powerapp changes
  useEffect(() => {
    if (!selectedPowerappId) return;

    const loadInitialData = async () => {
      try {
        await fetchAgentPowerappApprovals({
          powerappId: selectedPowerappId,
          params: {
            page: currentPage,
            per_page: itemsPerPage
          }
        });
      } catch (error) {
        console.error('Error loading approval data:', error);
        toast.error('Error loading approval details', { duration: 5000 });
      }
    };

    loadInitialData();
  }, [selectedPowerappId, currentPage, itemsPerPage]);

  const onCloseClick = useCallback(() => {
    setSelectedPowerappId(null);
    setSelectedPowerapp(null);
    setPowerappApprovals([]);
    toggleShowDetails();
    if (expandDetails) {
      toggleExpandDetails();
    }
  }, [
    expandDetails,
    toggleExpandDetails,
    toggleShowDetails,
    setSelectedPowerappId,
    setSelectedPowerapp,
    setPowerappApprovals,
  ]);

  const items = useMemo(() => {
    return powerappApprovals || [];
  }, [powerappApprovals]);

  const getStatusClass = (status) => {
    const statusLower = status?.toLowerCase() || '';
    switch (statusLower) {
      case 'pending':
        return styles.statusPending;
      case 'approved':
        return styles.statusApproved;
      case 'declined':
        return styles.statusDeclined;
      default:
        return '';
    }
  };

  return (
    <div className={`${styles.details} ${expandDetails ? styles.expanded : ''}`}>
      <div className={styles.details_head}>
        <button
          className={styles.expand_button}
          onClick={toggleExpandDetails}
          type="button"
        >
          {!expandDetails ? <ExpandDetailsIcon /> : <CollapseDetailsIcon />}
          <span>{!expandDetails ? 'Expand Details' : 'Minimize'}</span>
        </button>

        <ButtonIcon onClick={onCloseClick}>
          <CloseIcon24 />
        </ButtonIcon>
      </div>

      <div className={styles.merchantInfo}>
        <div className={styles.merchantInfoTop}>
          <div className={styles.merchantNameArea}>
            <div className={styles.merchantNameContainer}>
              <p className={styles.merchantName}>{selectedPowerapp?.dba || '-'}</p>
            </div>
          </div>
        </div>
        <div className={styles.infoRow}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>App ID:</span>
            <div className={styles.infoValue}>
              <span>{selectedPowerapp?.id || '-'}</span>
              <Copier text={selectedPowerapp?.id || ''}>
                <CopyIcon />
              </Copier>
            </div>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Organization:</span>
            <div className={styles.infoValue}>
              <span>{selectedPowerapp?.organization_name || '-'}</span>
              <Copier text={selectedPowerapp?.organization_name || ''}>
                <CopyIcon />
              </Copier>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.approvalsList}>
        {items.map((item) => (
          <div key={item.id} className={styles.approvalItem}>
            <div className={styles.approvalHeader}>
              <div className={styles.approvalType}>
                <span className={styles.label}>Type:</span>
                <span className={styles.value}>{item.action_type || '-'}</span>
              </div>
              <div className={styles.approvalStatus}>
                <span className={getStatusClass(item.status)}>{item.status}</span>
              </div>
            </div>
            <div className={styles.approvalDetails}>
              <div className={styles.approvalDescription}>
                {item.description || '-'}
              </div>
              {item.notes && (
                <div className={styles.approvalNotes}>
                  <span className={styles.label}>Notes:</span>
                  <span className={styles.value}>{item.notes}</span>
                </div>
              )}
              <div className={styles.approvalDates}>
                <div className={styles.dateItem}>
                  <span className={styles.label}>Submitted:</span>
                  <span className={styles.value}>
                    {new Date(item.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className={styles.dateItem}>
                  <span className={styles.label}>Last Updated:</span>
                  <span className={styles.value}>
                    {new Date(item.updated_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className={styles.noApprovals}>
            No approval requests found
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentApprovalDetails;